import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { AlertCircle, Check, CheckCircle, Info, Package, X } from "lucide-react";
import { useFormik } from "formik";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcInputNumber from "shared/NcInputNumber";
import toast from "react-hot-toast";
import { submitEnquiryForm } from "store/slices/enquiryFormSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/Store";
import { getUserId } from "services/cookieService";
import {
  registerGuestUser,
  loginGuestUser,
  resendVerificationCode,
  verifyOtp,
} from "store/slices/authSlice";
import { selectAuthLoading, selectUser } from "store/slices/authSlice";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "store/hooks/hooks";
import Truncate from "utils/Truncate";
import {
  IBulkEnquiryProduct,
  IEnquiryPayload,
} from "Interfaces/interface.enquirypayload";
import { openModal } from "store/slices/authModalSlice";
import {
  filterImageByAttributes,
  getProductNameWithAttributes,
} from "utils/AttributeHandlers";
import {
  COMPANY_NAME_PATTERN,
  DESCRIPTION_PATTERN,
  EMAIL_PATTERN,
  MOBILE_PATTERN,
  NAME_PATTERN,
} from "utils/validationRegex";
import { Alert, AlertDescription } from "@/components/UI/alert";
import { GlowingBadge } from "shared/Badge/GlowingBadge";
import { isKit, ITEM_TYPES } from "utils/itemTypeHelpers";


const getFormSchema = (userData: {
  fullName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
}) =>
  z.object({
    name: z
      .string({
        required_error: "Name is required",
        invalid_type_error: "Name is required",
      })
      .min(1, "Company Name must be at least 1 characters")
      .max(255, "Name must be less than 255 characters")
      .regex(NAME_PATTERN, "Invalid name"),

    companyName: z
      .string({
        required_error: "Company Name is required",
        invalid_type_error: "Company Name is required",
      })
      .min(2, "Company Name must be at least 2 characters")
      .max(255, "Company Name must be less than 255 characters")
      .regex(COMPANY_NAME_PATTERN, "Please enter a valid company name"),

    email: z
      .string({
        required_error: "Email is required",
        invalid_type_error: "Email is required",
      })
      .min(1, "Email cannot be empty")
      .max(255, "Email must be less than 255 characters")
      .regex(EMAIL_PATTERN, "Please enter a valid email address"),

    phoneNumber: z
      .string({
        required_error: "Mobile number is required",
        invalid_type_error: "Mobile number is required",
      })
      .regex(MOBILE_PATTERN, "Please enter a valid 10-digit mobile number"),

    note: z
      .string({
        required_error: "Message is required",
        invalid_type_error: "Message is required",
      })
      .min(20, "Message must be greater than 20 characters")
      .max(500, "Message must be less than 500 characters")
      .regex(DESCRIPTION_PATTERN, "Please enter a valid message"),
  });

type EnquiryDialogueProps = {
  onClose: () => void;
  products: IBulkEnquiryProduct[];
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
};

const BulkEnquiryDialogue: React.FC<EnquiryDialogueProps> = ({
  onClose,
  products,
  isOpen = true,
  setIsOpen,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const authLoading = useAppSelector(selectAuthLoading);
  const productMinOrderQuantity = 25;
  const [quantitySelected, setQuantitySelected] = useState<{
    [key: string]: number;
  }>(
    products.reduce(
      (acc, item) => ({
        ...acc,
        [`${item.productId}-${item.variantId}`]:
          item.qty || productMinOrderQuantity,
      }),
      {}
    )
  );

  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [tempPassword, setTempPassword] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(!!user?.emailVerified);
  const [isAccepted, setIsAccepted] = useState(true);
  const [otpSentMessage, setOtpSentMessage] = useState<React.ReactNode>("");
  const userId = getUserId();
  const [gettingOtp, setIsGettingOtp] = useState(false);

  const cleanPhoneNumber = (rawPhone: string | null | undefined): string => {
    const phone = rawPhone ?? "";
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length > 10 ? digitsOnly.slice(-10) : digitsOnly;
  };

  const [userData, setUserData] = useState({
    fullName: user?.name || "",
    companyName: user?.company || "",
    email: user?.emailAddress || "",
    phoneNumber: cleanPhoneNumber(user?.phoneNumber),
    role: user?.role || "",
  });

  const formik = useFormik({
    initialValues: {
      name: userData.fullName || "",
      companyName: userData.companyName || "",
      email: userData.email || "",
      role: userData.role || "",
      phoneNumber: userData.phoneNumber || "",
      note: "",
    },
    validationSchema: toFormikValidationSchema(getFormSchema(userData)),
    onSubmit: async (values) => {
      try {
        if (!userId && !isEmailVerified) {
          const otpCode = otp.join("");
          if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
            toast.error("Please enter a valid 6-digit OTP.");
            return;
          }

          await dispatch(
            verifyOtp({
              type: "email",
              value: formik.values.email,
              code: otpCode,
            })
          ).unwrap();

          await dispatch(
            loginGuestUser({
              email: formik.values.email,
              password: tempPassword,
            })
          ).unwrap();

          setIsEmailVerified(true);
          setShowOTP(false);
          setOtp(["", "", "", "", "", ""]);
          setUserData({
            fullName: formik.values.name,
            companyName: formik.values.companyName,
            email: formik.values.email,
            phoneNumber: formik.values.phoneNumber,
            role: formik.values.role,
          });
        }

        const items = products.map((item) => ({
          productId: item.id,
          variantId: item.variantId,
          qty:
            quantitySelected[`${item.productId}-${item.variantId}`] ||
            productMinOrderQuantity,
          itemType: item.itemType || ITEM_TYPES.PRODUCTS,
          productGroupId: item.id,
        }));


        const payload: IEnquiryPayload = {
          name: values.name,
          companyName: values.companyName,
          email: values.email,
          role: values.role,
          phoneNumber: values.phoneNumber,
          note: values.note,
          items,
          userId: userId || user?.id || "",
        };

        const result = await dispatch(submitEnquiryForm(payload));
        if (submitEnquiryForm.fulfilled.match(result)) {
          toast.success("Enquiry submitted successfully!");
          formik.resetForm();
          localStorage.removeItem("bulkEnquiryIntent");
          onClose();
          if (setIsOpen) setIsOpen(false);
        } else {
          toast.error(result.payload || "Failed to submit enquiry");
        }
      } catch (error) {
        console.log(error);
      }
    },
  });

  const handleCloseDialogue = useCallback(() => {
    localStorage.removeItem("bulkEnquiryIntent");
    onClose();
    if (setIsOpen) setIsOpen(false);
  }, [onClose, setIsOpen]);

  const handleOtpChange = (index: number, value: string) => {
    if (/^\d$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      } else if (value === "" && index > 0) {
        const prevInput = document.getElementById(
          `otp-${index - 1}`
        ) as HTMLInputElement;
        if (prevInput) prevInput.focus();
      }
    }
  };

  const handleGetOtp = async (e: React.MouseEvent) => {
    setIsGettingOtp(true);
    e.preventDefault();
    if (
      !formik.values.name.trim() ||
      !formik.values.email.trim() ||
      !formik.values.companyName.trim() ||
      !formik.values.phoneNumber.trim() ||
      !!formik.errors.email ||
      !!formik.errors.phoneNumber ||
      !!formik.errors.name ||
      !!formik.errors.companyName
    ) {
      toast.error("Please fill all required fields with valid data.");
      setIsGettingOtp(false);
      return;
    }

    localStorage.setItem(
      "bulkEnquiryIntent",
      JSON.stringify({
        products,
        quantities: quantitySelected,
        fromPath: location.pathname + location.search,
      })
    );

    try {
      const result = await dispatch(
        registerGuestUser({
          email: formik.values.email,
          phoneNumber: formik.values.phoneNumber,
          name: formik.values.name,
          companyName: formik.values.companyName,
        })
      ).unwrap();
      setTempPassword(result.tempPassword);
      setShowOTP(true);
      setOtpSentMessage(
        <div>
          OTP sent to <Truncate text={formik.values.email} maxLength={30} />
        </div>
      );
      toast.success(`OTP sent to ${formik.values.email}`, {
        style: {
          wordBreak: "break-all",
        },
      });
      setIsGettingOtp(false);
    } catch (err: any) {
      console.log("Guest registration error:", err);
      setIsGettingOtp(false);
      if (err?.errorCode === 409) {
        dispatch(openModal());
      } else {
        // toast.error(err?.message || "Failed to send OTP");
      }
    }
  };

  const handleResendOtp = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await dispatch(
        resendVerificationCode({
          type: "email",
          value: formik.values.email,
        })
      ).unwrap();
      setOtpSentMessage(`OTP resent to ${formik.values.email}`);
      toast.success(`OTP resent to ${formik.values.email}`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to resend OTP");
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseDialogue();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleCloseDialogue]);

  useEffect(() => {
    if (user) {
      setUserData({
        fullName: user.name || "",
        companyName: user.company || "",
        email: user.emailAddress || "",
        phoneNumber: cleanPhoneNumber(user.phoneNumber),
        role: user.role || "",
      });
      formik.setValues({
        name: user.name?.trim() || formik.values.name,
        companyName: user.company || formik.values.companyName,
        email: user.emailAddress || formik.values.email,
        role: user.role || formik.values.role,
        phoneNumber:
          cleanPhoneNumber(user.phoneNumber) || formik.values.phoneNumber,
        note: formik.values.note,
      });
      setIsEmailVerified(!!user.emailVerified);
    }
  }, [user]);

  return (
    <div
      className={`fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4 sm:p-6 md:p-8 ${!isOpen ? "hidden" : ""}`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.5, ease: "easeOut" as const }}
        className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl flex flex-col border border-gray-200 max-h-[90vh] md:max-h-[95vh]"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-black py-4 border-b border-gray-200 text-center">
          Place Enquiry
        </h2>
        <button
          onClick={handleCloseDialogue}
          disabled={formik.isSubmitting || authLoading}
          className={`absolute text-gray-600 p-2 rounded-full z-50 top-1 md:top-3 right-1 md:right-3 duration-300 ${formik.isSubmitting || authLoading
            ? "cursor-not-allowed opacity-50"
            : "hover:text-red-400 hover:bg-red-100"
            }`}
        >
          <X size={20} />
        </button>

        {!user?.id && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mx-6 mb-4 mt-5">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-purple-800">
                <strong>Quick note:</strong> We’ll verify your entered email. If
                you don’t have an account, we’ll create one. If you already
                have, we’ll ask you to sign in.
              </p>
            </div>
          </div>
        )}
        <div className="w-full px-5">
          <Alert className="mt-4 border-blue-200 bg-blue-50">
            <AlertDescription className="text-blue-600">
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-500" />
                <span>
                  Listed prices are exclusive of{" "}
                  <strong className="font-semibold">
                    GST, Branding, & Delivery
                  </strong>{" "}
                  costs
                </span>
              </p>
            </AlertDescription>
          </Alert>
        </div>
        <div className="flex flex-col md:flex-row w-full h-full min-h-0 overflow-y-auto md:overflow-hidden">
          {/* Products Section */}
          <div className="w-full md:w-1/2 md:border-r border-gray-200 flex flex-col max-h-[35vh] md:max-h-full flex-shrink-0">
            <div className="p-4 md:p-6 pb-2 md:pb-4 border-b border-gray-100 flex-shrink-0 bg-white z-10">
              <h2 className="text-lg md:text-xl font-medium text-gray-700">Products</h2>
            </div>
            <div className="overflow-y-auto p-4 md:p-6 pt-2 h-full custom-scrollbar">
              {products.map((item: any) => {
                const variantAttributes = item.variants?.[0]?.attributes || {};

                let imageUrl;

                if (
                  item.images &&
                  Array.isArray(item.images) &&
                  typeof item.images[0] === "string"
                ) {
                  imageUrl = item.images[0];
                } else {
                  const matchingImage = filterImageByAttributes(
                    item,
                    variantAttributes
                  );
                  imageUrl = matchingImage
                    ? matchingImage.images[0]
                    : item.images[0]?.images[0];
                }
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mb-3 last:mb-0"
                    key={`${item.productId}-${item.variantId}`}
                  >
                    <div className="group border border-gray-100 rounded-lg p-3 hover:shadow-sm transition-shadow bg-white">
                      <div className="flex items-start gap-3">
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <img
                            className="w-16 h-16 rounded-md object-contain border border-gray-100 bg-gray-50 nature-img"
                            src={imageUrl}
                            alt={item.name}
                          />
                        </div>

                        {/* Content Wrapper */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {isKit(item) && (
                                <GlowingBadge
                                  icon={Package}
                                  text="Kit"
                                  bgColor="bg-blue-600"
                                  textColor="text-white"
                                  className="!py-0 !px-1.5 !text-[10px] scale-90 origin-left"
                                />
                              )}
                            </div>
                            <div className="font-medium text-sm text-gray-900 truncate leading-tight">
                              <Truncate text={item.name} maxLength={50} />
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-900 font-bold text-sm">₹{item.price?.toLocaleString()}</span>
                                {getProductNameWithAttributes(item)}
                              </div>
                              <NcInputNumber
                                key={`${item.productId}-${item.variantId}`}
                                min={productMinOrderQuantity}
                                defaultValue={
                                  quantitySelected[
                                  `${item.productId}-${item.variantId}`
                                  ] || productMinOrderQuantity
                                }
                                onChange={(val) =>
                                  setQuantitySelected((prev) => ({
                                    ...prev,
                                    [`${item.productId}-${item.variantId}`]:
                                      val,
                                  }))
                                }
                                size="sm"
                                widthClass="w-[100px]"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="w-full md:w-1/2 flex flex-col flex-1 min-h-0 bg-gray-50/50 md:bg-white">
            <div className="flex-1 p-4 md:p-6 pt-4 custom-scrollbar md:overflow-y-auto">
              <form
                onSubmit={formik.handleSubmit}
                className="md:grid md:grid-cols-2 gap-4 gap-y-0"
              >
                <div className="mb-3">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50 ${formik.touched.name && formik.errors.name
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="Your Name"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.name}
                    </p>
                  )}
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="companyName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50 ${formik.touched.companyName && formik.errors.companyName
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="Your Company"
                  />
                  {formik.touched.companyName && formik.errors.companyName && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.companyName}
                    </p>
                  )}
                </div>

                <div className="mb-3 flex flex-col">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50 ${formik.touched.email && formik.errors.email
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="name@company.com"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.email}
                    </p>
                  )}
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting}
                    className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50 ${formik.touched.phoneNumber && formik.errors.phoneNumber
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="1234567890"
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.phoneNumber}
                    </p>
                  )}
                </div>

                <div className="col-span-2 flex justify-end">
                  {!userId && !showOTP && !isEmailVerified && (
                    <div className="flex items-center justify-end w-20">
                      <ButtonPrimary
                        onClick={handleGetOtp}
                        disabled={
                          !formik.values.name.trim() ||
                          !formik.values.email.trim() ||
                          !formik.values.companyName.trim() ||
                          !formik.values.phoneNumber.trim() ||
                          !!formik.errors.email ||
                          !!formik.errors.phoneNumber ||
                          !!formik.errors.name ||
                          !!formik.errors.companyName ||
                          gettingOtp
                        }
                        loading={gettingOtp}
                        className="transition duration-300 disabled:cursor-not-allowed"
                      >
                        {!gettingOtp && "Get Email OTP"}
                      </ButtonPrimary>
                    </div>
                  )}
                </div>

                <div className="mb-3 col-span-2">
                  <label
                    htmlFor="role"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Role (Optional)
                  </label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting}
                    className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50 ${formik.touched.role && formik.errors.role
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="e.g. Admin, Sales"
                  />
                  {formik.touched.role && formik.errors.role && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.role}
                    </p>
                  )}
                </div>

                <div className="mb-1 col-span-2">
                  <label
                    htmlFor="note"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Message *
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    rows={4}
                    value={formik.values.note}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting || (!userId && !showOTP)}
                    className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50 ${formik.touched.note && formik.errors.note
                      ? "border-red-500"
                      : "border-gray-300"
                      } ${!userId && !showOTP
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                      }`}
                    placeholder="How can we personalize this for you? Mention quantity, branding, delivery dates, or special notes."
                  ></textarea>
                  {formik.touched.note && formik.errors.note && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.note}
                    </p>
                  )}
                </div>

                {!userId && showOTP && !isEmailVerified && (
                  <div className="mb-3 col-span-2 flex flex-col gap-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Enter OTP
                    </label>
                    <div className="lg:flex gap-8 justify-between items-center">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              id={`otp-${index}`}
                              type="text"
                              maxLength={1}
                              disabled={formik.isSubmitting}
                              value={digit}
                              onChange={(e) =>
                                handleOtpChange(index, e.target.value)
                              }
                              className="w-9 h-9 text-center border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                              placeholder="0"
                            />
                          ))}
                        </div>
                        {otpSentMessage && (
                          <p className="text-sm text-gray-600">
                            {otpSentMessage}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={authLoading}
                          className={`text-sm text-blue-600 hover:underline ${authLoading ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                          Resend OTP
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {!userId && (
                  <div className="col-span-2 mb-3">
                    <div className="flex items-start gap-3">
                      <div
                        onClick={() => setIsAccepted(!isAccepted)}
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 mt-0.5 cursor-pointer ${isAccepted
                          ? "bg-green-600 border-green-600"
                          : "border-gray-300 bg-white hover:border-green-400"
                          }`}
                      >
                        {isAccepted && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-gray-700 leading-snug">
                          I have read and agree to 360customizer's{" "}
                          <a
                            href="/terms-of-use"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline"
                          >
                            Terms of Use
                          </a>{" "}
                          and{" "}
                          <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline"
                          >
                            Privacy Policy
                          </a>
                          .
                        </div>
                        {!isAccepted && (
                          <div className="mt-2 text-xs text-red-500 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            <span>Acceptance is required to proceed</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-span-2 text-right mt-4">
                  <ButtonPrimary
                    type="submit"
                    loading={formik.isSubmitting}
                    disabled={
                      formik.isSubmitting ||
                      authLoading ||
                      (!userId && !showOTP) || // Disable submit until OTP is requested
                      (!userId && !isAccepted) ||
                      (!userId &&
                        showOTP &&
                        !isEmailVerified &&
                        otp.join("").length !== 6) ||
                      !formik.values.name.trim() ||
                      !formik.values.email.trim() ||
                      !formik.values.companyName.trim() ||
                      !formik.values.phoneNumber.trim() ||
                      !formik.values.note.trim() ||
                      !!formik.errors.name ||
                      !!formik.errors.email ||
                      !!formik.errors.companyName ||
                      !!formik.errors.phoneNumber ||
                      !!formik.errors.note
                    }
                    className="transition duration-200 w-auto shadow-none disabled:cursor-not-allowed disabled:text-gray-800"
                    sizeClass="max-w-[20rem] min-w-[15rem]"
                  >
                    {!formik.isSubmitting &&
                      !authLoading &&
                      !userId &&
                      showOTP &&
                      !isEmailVerified
                      ? "Verify and Submit"
                      : "Submit Enquiry"}
                  </ButtonPrimary>
                </div>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BulkEnquiryDialogue;
